/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { BASE_UNITS, type BaseUnit, type StockUnit } from "./types";
import { UnitsEditor, validUnits } from "./UnitsEditor";

export type NewStockItem = {
  name: string;
  base_unit: BaseUnit;
  stock_category: string | null;
  units: StockUnit[];
};

type Props = {
  saving: boolean;
  onClose: () => void;
  onAdd: (item: NewStockItem) => void;
};

export const AddStockItemModal = defineComponent<Props>({
  name: "AddStockItemModal",
  props: ["saving", "onClose", "onAdd"],
  setup(props) {
    const { t } = useLocalization();
    const name = ref("");
    const baseUnit = ref<BaseUnit>("piece");
    const category = ref("");
    const units = ref<StockUnit[]>([]);

    const submit = () => {
      if (!name.value.trim()) return;
      props.onAdd({
        name: name.value.trim(),
        base_unit: baseUnit.value,
        stock_category: category.value.trim() || null,
        units: validUnits(units.value),
      });
    };

    return () => (
      <LemmonModal
        title={t("inventory.stock.add_title")}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose()}>
                {t("general.words.cancel")}
              </button>
              <button
                class="btn btn-primary"
                disabled={!name.value.trim() || props.saving}
                onClick={submit}
              >
                {t("inventory.stock.add_item")}
              </button>
            </div>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          <label class="block">
            <span class="mb-1 block text-sm font-medium">{t("inventory.stock.col_name")}</span>
            <input
              class="input input-bordered w-full"
              placeholder={t("inventory.stock.name_placeholder")}
              v-model={name.value}
              autofocus
            />
          </label>
          <label class="block">
            <span class="mb-1 block text-sm font-medium">
              {t("inventory.stock.col_base_unit")}
            </span>
            <select class="select select-bordered w-full" v-model={baseUnit.value}>
              {BASE_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {t(`inventory.stock.base_unit_${unit}`)}
                </option>
              ))}
            </select>
            <span class="mt-1 block text-xs text-base-content/50">
              {t("inventory.stock.base_unit_help")}
            </span>
          </label>
          <label class="block">
            <span class="mb-1 block text-sm font-medium">
              {t("inventory.stock.col_category")}
            </span>
            <input class="input input-bordered w-full" v-model={category.value} />
          </label>
          <div>
            <span class="mb-1 block text-sm font-medium">{t("inventory.stock.col_units")}</span>
            <UnitsEditor
              baseUnit={baseUnit.value}
              units={units.value}
              onChange={(next: StockUnit[]) => (units.value = next)}
            />
          </div>
        </div>
      </LemmonModal>
    );
  },
});
